import { useEffect, useState } from "react";

import InfoBox from "@/components/InfoBox";
import { Source } from "@/constants";
import { useLocalAudio } from "@/containers/MentionCard/LocalAudioContext";
import { AudioMentionV4 } from "@/types/MentionCard";
import getClosestMention from "@/utils/getClosestMention";
import AccessControl from "@components/AccessControl";
import Karaoke from "@components/Karaoke/index_v4";
import PlayButtonWithControls from "@components/PlayButtonWithControls";

import { useMentionCard } from "../../../context";
import Cover from "../../Cover";
import Favorite from "../../Favorite";
import Footer from "../../Footer";
import Header from "../../Header";
import MetaActions from "../../MetaActions";
import PaywallNotice from "../../PaywallNotice";
import ShareEpisode from "../../ShareEpisode";
import TextMentions from "../Text/TextMentions";
import Mention from "./Mention";
import Timeline from "./Timeline";

import styles from "./Audio.module.css";
const AudioMentionCardBody = () => {
  // Handle audio player, timeline etc here.
  const {
    audioMentions = [],
    textMentions = [],
    hideActions,
    can_mark_favorite,
    channel: { channel_type: source, artwork_url: channelCover },
    episode: {
      artwork_url: episodeCover,
      external_media_source: episodeUrl,
      guid: episodeGuid,
      audio_url: episodeAudioUrl,
    },
  } = useMentionCard();

  const cover = episodeCover || channelCover;

  const [currentMention, setCurrentMention] = useState<AudioMentionV4>(
    audioMentions[0]
  );

  const [manualTime, setManualTime] = useState<number | undefined>(undefined);

  useEffect(() => {
    setCurrentMention(audioMentions[0]);
  }, [audioMentions]);

  // Display the current mention start time or the current playing time:

  const { audio, playing, currentTime, errored, loading, paused } =
    useLocalAudio({
      currentTime: true,
      playing: true,
      errored: true,
      loading: true,
      paused: true,
    });

  // Prefer the manual time when no audio object exists
  const effectiveTime: number = audio
    ? (currentTime ?? 0)
    : (manualTime ?? currentTime ?? 0);

  useEffect(() => {
    const m = getClosestMention(audioMentions, effectiveTime); // now always number
    m && setCurrentMention(m as AudioMentionV4);
  }, [audioMentions, effectiveTime]);

  if (audioMentions.length === 0) return null;

  const hasPreviousMention =
    audioMentions.length > 1 && audioMentions.indexOf(currentMention) - 1 >= 0;
  const hasNextMention =
    audioMentions.length > 1 &&
    audioMentions.indexOf(currentMention) + 1 < audioMentions.length;

  const seekPreviousMention = () => {
    if (hasPreviousMention) {
      const i = audioMentions.indexOf(currentMention) - 1;
      audio.seek(audioMentions[i].context_start_timing);
    }
  };

  const seekNextMention = () => {
    if (hasNextMention) {
      const i = audioMentions.indexOf(currentMention) + 1;
      audio.seek(audioMentions[i].context_start_timing);
    }
  };

  return (
    <div className={styles.root} data-testid="audio-episode">
      <div className={styles.top}>
        <div className={styles.inner}>
          <Header />
          <PaywallNotice />
          <MetaActions />
          {textMentions.length > 0 && (
            <TextMentions
              hideActions
              hideExternalLink
              hideMetadata
              visibleMentions={1}
            />
          )}

          {!episodeAudioUrl ? (
            <InfoBox text={L.MENTIONS_AUDIO_UNAVAILABLE} />
          ) : null}

          <Timeline
            visible={currentMention}
            onMarkerClick={(clicked) => {
              setCurrentMention(clicked);

              if (episodeAudioUrl && audio) {
                audio.seek(clicked.context_start_timing);
                setManualTime(undefined); // audio will drive Karaoke again
              } else {
                setManualTime(clicked.context_start_timing); // manual highlight
              }
            }}
          />
          <div className={styles.content}>
            {episodeAudioUrl ? (
              <PlayButtonWithControls
                error={errored}
                loading={loading}
                playing={playing}
                onClick={() => {
                  if (playing) return audio.pause();
                  if (paused) return audio.play();
                  audio.play(currentMention.context_start_timing);
                }}
                onClickPrevious={seekPreviousMention}
                onClickNext={seekNextMention}
                previousDisabled={!hasPreviousMention}
                nextDisabled={!hasNextMention}
              />
            ) : null}
            <div>
              <div className={styles.transcript}>
                <Karaoke manualCurrentTime={manualTime} />
              </div>
              {currentMention && <Mention mention={currentMention} />}
            </div>
          </div>
        </div>

        <div className={styles.cover}>
          <Cover
            source={source as Source}
            cover={cover}
            episodeUrl={episodeUrl}
            episodeGuid={episodeGuid}
          />

          {!hideActions && can_mark_favorite && (
            <AccessControl>
              <Favorite className={styles.favorite} />
            </AccessControl>
          )}
          <ShareEpisode className={styles.share} />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AudioMentionCardBody;
